import React from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { Languages, Sparkles, Users, FileDown, ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758691736764-2a88e313b1f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHNlc3Npb258ZW58MHx8fHwxNzg2NTE1NDk5fDA&ixlib=rb-4.1.0&q=85";

const Feature = ({ icon: Icon, title, desc, i }) => (
  <div
    className="card-amt p-6 animate-fade-up"
    style={{ animationDelay: `${i * 90}ms`, opacity: 0 }}
    data-testid={`feature-card-${i}`}
  >
    <div
      className="w-10 h-10 rounded-sm flex items-center justify-center mb-4"
      style={{ background: "rgba(201,165,87,0.12)", color: "var(--gold)" }}
    >
      <Icon size={20} />
    </div>
    <h3 className="font-heading text-lg mb-1" style={{ color: "var(--text)" }}>
      {title}
    </h3>
    <p className="text-sm" style={{ color: "var(--text-dim)" }}>
      {desc}
    </p>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useAuth();

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-5 sm:px-8">
        {/* hero */}
        <section className="grid lg:grid-cols-2 gap-10 items-center pt-14 pb-20">
          <div className="animate-fade-up" style={{ opacity: 0 }}>
            <span
              className="text-xs uppercase tracking-[0.25em]"
              style={{ color: "var(--gold)" }}
              data-testid="hero-tag"
            >
              {t("hero_tag")}
            </span>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 mb-5 leading-[1.05]"
              style={{ color: "var(--text)" }}
            >
              {t("hero_title")}
            </h1>
            <p className="text-base sm:text-lg leading-relaxed max-w-xl" style={{ color: "var(--text-dim)" }}>
              {t("hero_sub")}
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <button
                data-testid="hero-start-btn"
                onClick={() => navigate("/test")}
                className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-base"
              >
                {t("hero_cta")} <ArrowRight size={18} />
              </button>
              <button
                data-testid="hero-trainer-btn"
                onClick={() => navigate("/login")}
                className="btn-outline-gold px-6 py-3 text-base"
              >
                {t("hero_cta2")}
              </button>
            </div>
          </div>
          <div className="relative animate-fade-in" style={{ opacity: 0 }}>
            <div
              className="absolute -inset-3 rounded-sm"
              style={{ border: "1px solid var(--border)" }}
            />
            <img
              src={HERO_IMG}
              alt="Corporate training session"
              className="relative w-full h-[420px] object-cover rounded-sm"
              style={{ filter: "saturate(0.9)" }}
            />
            <div
              className="absolute inset-0 rounded-sm"
              style={{ background: "linear-gradient(180deg, rgba(14,31,51,0.2), rgba(14,31,51,0.65))" }}
            />
          </div>
        </section>

        {/* features */}
        <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 pb-24">
          <Feature i={0} icon={Languages} title={t("feat_1_t")} desc={t("feat_1_d")} />
          <Feature i={1} icon={Sparkles} title={t("feat_2_t")} desc={t("feat_2_d")} />
          <Feature i={2} icon={Users} title={t("feat_3_t")} desc={t("feat_3_d")} />
          <Feature i={3} icon={FileDown} title={t("feat_4_t")} desc={t("feat_4_d")} />
        </section>
      </main>
      <footer className="border-t py-8 text-center text-xs" style={{ borderColor: "var(--border)", color: "var(--text-dim)" }}>
        © {new Date().getFullYear()} amt consulting
      </footer>
    </div>
  );
}
